import { flip, offset, shift, useClick, useFloating, useInteractions, useRole } from "@floating-ui/react";
import { endOfMonth, isSameMonth, startOfMonth, subMonths } from "date-fns";
import { useMemo, useState } from "react";

import { INVOICES_DELIVERY_PERIOD_MIN_DATE } from "~/modules/billing/constants/billingConstants.ts";
import { formatSmartDateRange } from "~/utils/dateAndTimeUtils.ts";

const now = new Date();

export type TempDateRange = [Date | null, Date | null];

export interface QuickSelectOption {
	label: string;
	start: Date;
}

export interface UseDeliveryPeriodPickerOptions {
	startDate: Date | null;
	endDate: Date | null;
}

export interface UseDeliveryPeriodPickerReturn {
	isOpen: boolean;
	setIsOpen: (open: boolean) => void;
	refs: ReturnType<typeof useFloating>["refs"];
	floatingStyles: ReturnType<typeof useFloating>["floatingStyles"];
	context: ReturnType<typeof useFloating>["context"];
	getReferenceProps: ReturnType<typeof useInteractions>["getReferenceProps"];
	getFloatingProps: ReturnType<typeof useInteractions>["getFloatingProps"];
	tempDateRange: TempDateRange;
	setTempDateRange: (range: TempDateRange) => void;
	quickSelectOptions: QuickSelectOption[];
	activeQuickSelect: string | null;
	buttonText: string;
	now: Date;
}

export function useDeliveryPeriodPicker({
	startDate,
	endDate,
}: UseDeliveryPeriodPickerOptions): UseDeliveryPeriodPickerReturn {
	const [isOpen, setIsOpen] = useState(false);
	const { refs, floatingStyles, context } = useFloating({
		open: isOpen,
		onOpenChange: setIsOpen,
		middleware: [offset(4), flip(), shift()],
		placement: "top-start",
	});

	const [tempDateRange, setTempDateRange] = useState<TempDateRange>([
		startDate,
		endDate,
	]);

	const quickSelectOptions = useMemo<QuickSelectOption[]>(() => [
		{ label: "3 Monate", start: startOfMonth(subMonths(now, 2)) },
		{ label: "1 Jahr", start: startOfMonth(subMonths(now, 11)) },
		{ label: "Gesamter Zeitraum", start: startOfMonth(INVOICES_DELIVERY_PERIOD_MIN_DATE) },
	], []);

	const activeQuickSelect = useMemo(() => {
		const [tempStart] = tempDateRange;
		if (!tempStart) return null;
		return quickSelectOptions.find(opt => isSameMonth(opt.start, tempStart))?.label ?? null;
	}, [tempDateRange, quickSelectOptions]);

	const buttonText = useMemo(() => {
		if (!startDate || !endDate) {
			return "Leistungszeitraum wählen";
		}
		return formatSmartDateRange(startDate, endDate, {
			month: "MMMM",
			year: " yyyy",
		});
	}, [endDate, startDate]);

	const click = useClick(context);
	const role = useRole(context);
	const { getReferenceProps, getFloatingProps } = useInteractions([click, role]);

	return {
		isOpen,
		setIsOpen,
		refs,
		floatingStyles,
		context,
		getReferenceProps,
		getFloatingProps,
		tempDateRange,
		setTempDateRange,
		quickSelectOptions,
		activeQuickSelect,
		buttonText,
		now,
	};
}

export function computeDateRange(tempStart: Date | null, tempEnd: Date | null): {
	startDate: Date | null;
	endDate: Date | null;
} {
	if (!tempStart) {
		return { startDate: null, endDate: null };
	}
	const newStartDate = startOfMonth(tempStart);
	const newEndDate = tempEnd ? endOfMonth(tempEnd) : endOfMonth(tempStart);
	return { startDate: newStartDate, endDate: newEndDate };
}